import React from 'react';
import { Box, xcss, Stack, Text } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { ItsmSpotlightCard } from '@atlassian/jira-servicedesk-itsm-onboarding-common/src/common/ui/itsm-spotlight-card/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useFlagsService } from '@atlassian/jira-flags';
import ProjectSummaryOnboardingImage from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/jsm-project-summary/index.tsx';
import { withChoreographedRender } from '@atlassian/jira-choreographer-services/src/withChoreographedRender.tsx';
import { useJiraServiceManagementProjectNavigation } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/project-sidebar-navigation-resource/index.tsx';
import { toggleFeature } from '../../../../services/project-summary-onboarding/index.tsx';
import SparklesIcon from './sparkle-icon/index.tsx';
import { messages } from './messages.tsx';

type Props = {
	projectKey: string;
	onMarkProjectSummaryOnboardingAsSeen: () => void;
	setIsOpen: (isOpen: boolean) => void;
};

const READ_MORE_LINK =
	'https://support.atlassian.com/jira-service-management-cloud/docs/gain-an-overview-of-your-work-with-the-project-summary-page/';

const ProjectSummaryOnboardingBase = ({
	projectKey,
	onMarkProjectSummaryOnboardingAsSeen,
	setIsOpen,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();
	const { refresh: refreshJSMNavigation } = useJiraServiceManagementProjectNavigation();

	const handleDismiss = () => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'projectSummaryOnboarding dismiss', {});
		onMarkProjectSummaryOnboardingAsSeen();
		setIsOpen(false);
	};

	const handleEnable = async () => {
		const toggleSuccess = await toggleFeature(projectKey);
		onMarkProjectSummaryOnboardingAsSeen();
		setIsOpen(false);
		if (toggleSuccess) {
			refreshJSMNavigation();
			fireTrackAnalytics(createAnalyticsEvent({}), 'projectSummaryOnboarding enable', {
				success: true,
			});
			showFlag({
				title: messages.toggleFeatureSuccessFlagTitle,
				description: [
					messages.toggleFeatureSuccessFlagDescription,
					{
						b: (chuck: string) => <b>{chuck}</b>,
					},
				],
				type: 'success',
				messageId:
					'servicedesk-queues-agent-view.layout.project-summary-onboarding.onboarding.show-flag.success.handle-enable.success',
				messageType: 'transactional',
			});
		} else {
			fireTrackAnalytics(createAnalyticsEvent({}), 'projectSummaryOnboarding enable', {
				success: false,
			});
			showFlag({
				title: messages.toggleFeatureErrorFlagTitle,
				description: [
					messages.toggleFeatureErrorFlagDescription,
					{
						b: (chuck: string) => <b>{chuck}</b>,
					},
				],
				type: 'error',
				messageId:
					'servicedesk-queues-agent-view.layout.project-summary-onboarding.onboarding.show-flag.error.handle-enable.failure',
				messageType: 'transactional',
			});
		}
	};

	return (
		<Stack
			xcss={onboardingStyles}
			testId="servicedesk-queues-agent-view.layout.project-summary-onboarding.onboarding.project-summary-onboarding"
		>
			<ProjectSummaryOnboardingImage alt="" />
			<Box xcss={spotlightCardStyles} paddingInline="space.300">
				<ItsmSpotlightCard
					buttons={{
						primary: {
							text: formatMessage(messages.enableButtonText),
							onClick: handleEnable,
						},
						secondary: {
							text: formatMessage(messages.dismissButtonText),
							onClick: handleDismiss,
						},
					}}
				>
					<Box xcss={spotlightContentStyles} backgroundColor="elevation.surface">
						<Heading size="small">
							{formatMessage(messages.title, {
								sparklesIcon: <SparklesIcon />,
							})}
						</Heading>
						<Text>{formatMessage(messages.adminBody)}</Text>
						<Link href={READ_MORE_LINK} target="_blank">
							{formatMessage(messages.readMoreLink)}
						</Link>
					</Box>
				</ItsmSpotlightCard>
			</Box>
		</Stack>
	);
};

const ChoreographerSpotlightCardWithImage = withChoreographedRender<Props>(
	ProjectSummaryOnboardingBase,
);

export default ChoreographerSpotlightCardWithImage;

const onboardingStyles = xcss({
	width: '320px',
	borderRadius: 'border.radius',
	overflow: 'hidden',
	zIndex: 'flag',
	boxShadow: 'elevation.shadow.overlay',
});

const spotlightCardStyles = xcss({
	marginLeft: 'space.050',
	marginTop: 'space.300',
	marginRight: 'space.050',
	marginBottom: 'space.300',
});

const spotlightContentStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	rowGap: 'space.100',
	marginRight: 'space.negative.100',
	marginBottom: 'space.negative.200',
});
